'use client';

import { useSyncExternalStore } from 'react';

import type { CtaProps } from '@types';

export const MAC_DOWNLOAD_HREF = '/download/mac';
export const MAC_DOWNLOAD_EMAIL_HREF = '/download/mac/email';

/**
 * A phone or tablet can't install the macOS app, so the installer link is a
 * dead end there. Detection is by device (user agent), not viewport width: a
 * narrow desktop window can still run the DMG.
 *
 * iPadOS reports a Mac user agent, so it is caught by its touch points.
 */
const isMobileDevice = (): boolean => {
  const uaData = (
    navigator as Navigator & { userAgentData?: { mobile?: boolean } }
  ).userAgentData;
  if (uaData?.mobile) return true;

  const ua = navigator.userAgent;
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;

  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
};

// The device never changes during a page's life, so there is nothing to
// subscribe to.
const subscribe = () => () => {};

/**
 * The href a “Download for Mac” CTA should point to on this device. The server
 * snapshot is the installer page, so desktop never flashes the mobile
 * destination; a phone or tablet switches to the page that emails the link
 * once it hydrates.
 */
export const useMacDownloadHref = (): CtaProps['href'] =>
  useSyncExternalStore(
    subscribe,
    () => (isMobileDevice() ? MAC_DOWNLOAD_EMAIL_HREF : MAC_DOWNLOAD_HREF),
    () => MAC_DOWNLOAD_HREF,
  );
